/**
 * FlightMap — the 3D flight path.
 *
 * MapLibre rather than Cesium: free, no vendor lock-in, no tile quota to
 * negotiate, and pitch plus an extruded altitude curtain gets most of the
 * readability of true 3D at a fraction of the integration cost. Swapping to
 * Cesium later means replacing this file and nothing else, which is why the
 * viewer talks to it through four methods and never touches MapLibre directly.
 *
 * The altitude curtain is the trick: one extruded quad per path segment,
 * rising from the ground to the aircraft's height. Tilt the camera and you
 * read the vertical profile directly off the map.
 */

import { droneSvg } from './attitude-model.js';

/**
 * Width of the flight path ribbon, in metres.
 *
 * Real-world rather than screen-space, so the ribbon thickens as you zoom in
 * exactly as the terrain does.
 *
 * Deliberately hairline. A wide ribbon is legible on a single out-and-back and
 * unreadable on a survey grid or an orbit, where dozens of passes overlap and
 * the path becomes a solid mass rather than a trace.
 */
const PATH_WIDTH_M = 0.5;

/** Flatter than it is wide, so it reads as a surface rather than a beam. */
const PATH_THICKNESS_M = PATH_WIDTH_M * 0.25;

/**
 * Rotor-tip to rotor-tip span of the drawn aircraft, in metres.
 *
 * Not life size, deliberately. A Mini is about 0.3 m across and an Inspire
 * about 1.2 m, and at the zoom a whole flight frames at, either is a couple of
 * pixels: too small to read an orientation from, which is the only reason the
 * glyph exists. Two and a half metres gives it about thirteen pixels there and
 * stays sane when zoomed in.
 *
 * The alternative, scaling with zoom to hold a constant pixel size, was tried
 * and abandoned: it makes the aircraft the only thing on the map that does not
 * obey the scale bar.
 *
 * An absolute size, not a multiple of the path width. It used to be the
 * latter, which broke the moment the path was thinned to a hairline: at 1.15
 * times a 0.1 m ribbon the aircraft would have been eleven centimetres across
 * and invisible at every useful zoom.
 *
 * The path wants to be as thin as it can be and still be seen. The aircraft
 * wants to be big enough to read its orientation. Those are different goals
 * and they need different numbers.
 */
const AIRCRAFT_SIZE_M = 2.5;

/**
 * Source options for the small, detailed geometry.
 *
 * A geojson source defaults to maxzoom 18 and a simplification tolerance of
 * 0.375 tile units. Both are sensible for country outlines and wrong for a
 * drone: at zoom 18 a rotor is under two pixels across, so it is simplified
 * to a handful of vertices at tiling time, and zooming past 18 magnifies that
 * simplified shape rather than retiling. The result is exactly what it looks
 * like, a small blocky approximation of a circle.
 *
 * maxzoom 22 keeps retiling as you zoom in; tolerance 0 keeps every vertex.
 * Affordable because there is so little of this geometry.
 */
const FINE_SOURCE = { maxzoom: 22, tolerance: 0 };

/** Where the curtain preference is kept. */
const CURTAIN_KEY = 'ddlv-map-curtain';

/**
 * Altitude covered by one curtain step, in metres.
 *
 * Sub-pixel at the zooms a whole flight frames at, so the staircase reads as
 * a ramp. There is no smoother option: fill-extrusion polygons are flat
 * topped by definition, and a genuinely sloped surface would mean a custom
 * WebGL layer.
 */
const STEP_METRES = 0.25;

/** Ceiling on curtain geometry, whatever the flight does. */
const MAX_CURTAIN_QUADS = 12000;

/**
 * Airframe colours.
 *
 * The front pair is a deeper green than the obvious one. Against a pale map a
 * bright mint reads as a highlight rather than as a part, and the whole point
 * of the colour is to be told apart from the red at a glance.
 */
const ROTOR_FRONT = '#B2DE27';
const ROTOR_REAR = '#F03434';
const BODY_GREY = '#a5aac4';
const ARM_GREY = '#c3c7db';
const NOSE_ACCENT = '#7367f0';

/**
 * Tile sources, overridable from index.html.
 *
 * The OpenStreetMap public endpoint is the default because it needs no
 * account, but their tile usage policy prohibits production traffic and it is
 * not a service to build on. Swapping provider should be a line of markup
 * rather than a code change:
 *
 *   <meta name="street-tiles" content="https://api.maptiler.com/...?key=...">
 *   <meta name="street-attribution" content="© MapTiler © OpenStreetMap">
 *   <meta name="street-maxzoom" content="22">
 */
const STREET_TILES = document.querySelector('meta[name="street-tiles"]')?.content
  || 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';

const STREET_ATTRIBUTION = document.querySelector('meta[name="street-attribution"]')?.content
  || '© OpenStreetMap contributors';

const STREET_MAXZOOM = Number(
  document.querySelector('meta[name="street-maxzoom"]')?.content
) || 19;

const SATELLITE_TILES = document.querySelector('meta[name="satellite-tiles"]')?.content
  || 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';

const SATELLITE_ATTRIBUTION = document.querySelector('meta[name="satellite-attribution"]')?.content
  || 'Imagery © Esri, Maxar, Earthstar Geographics';

/**
 * Base map styles.
 *
 * Two, because a flight path over streets and a flight path over imagery
 * answer different questions: one tells you which road you were following,
 * the other what you were actually looking at.
 */
const BASEMAPS = {
  map: {
    label: 'Map',
    style: {
      version: 8,
      sources: {
        osm: {
          type: 'raster',
          tiles: [STREET_TILES],
          tileSize: 256,

          // OpenStreetMap serves to zoom 19 and no further. Without this
          // MapLibre asks for zoom 20 anyway, gets a 404, and because an error
          // page carries no CORS header the browser reports it as a CORS
          // failure rather than a missing tile. The console fills with the
          // wrong diagnosis while the map simply goes blank.
          //
          // With it, MapLibre stretches the zoom 19 tiles instead: blurry when
          // you go right in, which is what every map does at its limit.
          maxzoom: STREET_MAXZOOM,
          attribution: STREET_ATTRIBUTION,
        },
      },
      layers: [
        { id: 'background', type: 'background', paint: { 'background-color': '#0B101B' } },
        {
          id: 'osm',
          type: 'raster',
          source: 'osm',
          paint: {
            // Was 0.55 opacity at -0.7 saturation, which read as fog. The map
            // is the context for the flight, so it has to be legible: street
            // names and field boundaries are half the reason for looking.
            // Held slightly back rather than dimmed, so the trace still wins.
            'raster-opacity': 0.88,
            'raster-saturation': -0.25,
            'raster-brightness-min': 0.05,
          },
        },
      ],
    },
  },

  satellite: {
    label: 'Satellite',
    style: {
      version: 8,
      sources: {
        imagery: {
          type: 'raster',
          tiles: [SATELLITE_TILES],
          tileSize: 256,

          // Esri publishes imagery to 19 in most places and further in some.
          // Capping here rather than discovering the ceiling as a wall of
          // console errors.
          maxzoom: 19,
          attribution: SATELLITE_ATTRIBUTION,
        },
      },
      layers: [
        { id: 'background', type: 'background', paint: { 'background-color': '#0B101B' } },
        {
          id: 'imagery',
          type: 'raster',
          source: 'imagery',
          // Barely touched. Imagery is already dark and detailed, and the
          // trace is a saturated blue that reads over it without help.
          paint: { 'raster-opacity': 0.95 },
        },
      ],
    },
  },
};

/** Where the basemap choice is kept. */
const BASEMAP_KEY = 'ddlv-map-basemap';

const API_BASE = document.querySelector('meta[name="api-base"]')?.content || '/api';

/**
 * Resolve the tile URLs. When the server has a MapTiler key configured it hands
 * back a short-lived signed token, and tiles are then fetched through our proxy
 * so the key never reaches the browser. Otherwise, and on any error, the public
 * OSM / Esri sources from the meta tags are used unchanged.
 */
async function resolveTileUrls() {
  try {
    const response = await fetch(`${API_BASE}/tile-token.php`, { cache: 'no-store' });
    if (!response.ok) return null;

    const t = await response.json();
    if (!t || !t.proxy || !t.expires || !t.token) return null;

    const q = `&expires=${encodeURIComponent(t.expires)}&token=${encodeURIComponent(t.token)}`;
    return {
      street: `${API_BASE}/tile.php?s=streets&z={z}&x={x}&y={y}${q}`,
      satellite: `${API_BASE}/tile.php?s=satellite&z={z}&x={x}&y={y}${q}`,
      streetMaxzoom: 20,
      attribution: '\u00A9 MapTiler \u00A9 OpenStreetMap contributors',
    };
  } catch {
    return null;
  }
}

/** BASEMAPS with the proxied tile URLs swapped in, or the originals if off. */
function basemapsWith(tiles) {
  if (!tiles) return BASEMAPS;

  const out = JSON.parse(JSON.stringify(BASEMAPS));
  out.map.style.sources.osm.tiles = [tiles.street];
  out.map.style.sources.osm.maxzoom = tiles.streetMaxzoom;
  out.map.style.sources.osm.attribution = tiles.attribution;
  out.satellite.style.sources.imagery.tiles = [tiles.satellite];
  out.satellite.style.sources.imagery.attribution = tiles.attribution;
  return out;
}

export class FlightMap {
  constructor(container, options = {}) {
    this.container = container;
    this.styleUrl = options.styleUrl || null;
    this.map = null;
    this.flight = null;
    this.ready = false;
    // Off by default: framing the whole flight is more useful than chasing
    // the aircraft, and following fights the initial fit.
    this.follow = false;
    this.basemaps = BASEMAPS;
  }

  /**
   * Returns false rather than throwing when MapLibre is unavailable, so the
   * rest of the viewer still works with the map panel showing a reason.
   */
  async init() {

    // MapLibre is fetched on demand: nothing on the landing page needs a map,
    // and its stylesheet was sitting in the critical rendering path.
    const { ensureMapLibre } = await import('./maplibre-loader.js');
    await ensureMapLibre();

    if (typeof maplibregl === 'undefined') return false;

    // Resolve tiles before the map is built: with a key configured this routes
    // through the signed proxy, otherwise it is the public sources unchanged.
    this.basemaps = basemapsWith(await resolveTileUrls());

    this.map = new maplibregl.Map({
      container: this.container,
      style: this.styleUrl || this.basemaps[this.basemap()].style,
      center: [0, 0],
      zoom: 1,
      pitch: 55,
      bearing: -20,
      attributionControl: { compact: true },
    });

    this.map.addControl(
      // The compass is off: it only ever resets pitch and bearing, and the
      // tilt control below does the job people actually reach for.
      new maplibregl.NavigationControl({ showCompass: false }),
      'top-right'
    );

    this.tiltControl = new TiltControl(this);
    this.map.addControl(this.tiltControl, 'top-right');
    this.curtainControl = new CurtainControl(this);
    this.map.addControl(this.curtainControl, 'top-right');

    this.basemapControl = new BasemapControl(this);
    this.map.addControl(this.basemapControl, 'top-right');

    await new Promise((resolve) => this.map.on('load', resolve));

    this.ready = true;

    // The altitude offset depends on pitch and zoom, so recompute it whenever
    // the view moves, not only when the row changes.
    this.map.on('move', () => this.updateAircraftLift());

    return true;
  }

  setFlight(flight) {
    if (!this.ready || !flight.quality.hasPosition) return false;

    this.flight = flight;
    this.clear();

    const path = flight.simplifiedPath(1.2);
    if (path.length < 2) return false;

    this.map.addSource('track', {
      ...FINE_SOURCE,
      type: 'geojson',
      data: {
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: path.map((p) => [p[0], p[1]]) },
      },
    });

    this._ribbonHeights = ribbonHeightByRow(path, flight.count);

    // One source, two layers.
    //
    // The curtain and the ribbon are the same stepped geometry read at
    // different heights: the curtain from the ground up to the path, the
    // ribbon as a thin cap sitting on top of it. Sharing the source means
    // they cannot drift out of alignment, which two independently generated
    // sets of quads eventually would.
    this.map.addSource('ribbon', {
      type: 'geojson',
      data: this.curtain(path),
      ...FINE_SOURCE,
    });

    // The curtain: a solid wall from the ground to the flight path.
    //
    // It occludes whatever is behind it when the camera is tilted, which is
    // why the default view is straight down: from directly above a vertical
    // wall has no width and hides nothing. Tilting is a deliberate act, and
    // the curtain can be switched off for it.
    this.map.addLayer({
      id: 'curtain',
      type: 'fill-extrusion',
      source: 'ribbon',
      layout: { visibility: this.curtainVisible() ? 'visible' : 'none' },
      paint: {
        'fill-extrusion-color': ['interpolate', ['linear'], ['get', 'height'],
          0, '#0263b8', 40, '#038AFF', 120, '#7367f0'],
        'fill-extrusion-height': ['get', 'height'],
        'fill-extrusion-base': 0,
        'fill-extrusion-opacity': 0.22,
        'fill-extrusion-vertical-gradient': true,
      },
    });

    // The ground track reads as a shadow: it locates the flight on the map
    // without competing with the path itself.
    this.map.addLayer({
      id: 'track',
      type: 'line',
      source: 'track',
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: { 'line-color': '#038AFF', 'line-width': 1, 'line-opacity': 0.35 },
    });

    // The path itself: a thin cap on top of the curtain.
    //
    // MapLibre has no 3D line primitive, so this is a shallow extruded slab.
    // Shallow matters: any thickness is a lie about where the aircraft was,
    // and from directly above a deep slab reads as a fat band rather than a
    // trace.
    this.map.addLayer({
      id: 'track3d',
      type: 'fill-extrusion',
      source: 'ribbon',
      layout: { visibility: this.curtainVisible() ? 'visible' : 'none' },
      paint: {
        'fill-extrusion-color': '#3aa5ff',
        'fill-extrusion-base': ['max', 0, ['-', ['get', 'height'], PATH_THICKNESS_M / 2]],
        'fill-extrusion-height': ['+', ['get', 'height'], PATH_THICKNESS_M / 2],
        'fill-extrusion-opacity': 0.95,
        // Flat shading across the top face. Without this the vertical sides
        // catch a different light to the cap and the ribbon reads as a solid
        // extruded beam rather than a surface.
        'fill-extrusion-vertical-gradient': false,
      },
    });

    this.addMarkers(flight);
    this.applyCurtain(this.curtainVisible());
    this.fit(path);

    return true;
  }

  /**
   * One extruded quad per segment. A quad rather than a line because
   * fill-extrusion needs a polygon with area; a zero-width strip renders as
   * nothing on some GPUs.
   */
  curtain(path) {
    return curtainGeometry(path);
  }

  addMarkers(flight) {
    const { home, takeoff } = flight.summary;
    const inRange = (p) => p
      && Number.isFinite(p.lat) && Number.isFinite(p.lon)
      && Math.abs(p.lat) <= 90 && Math.abs(p.lon) <= 180;

    // A garbage home point (some logs carry a huge sentinel) must not become the
    // marker or the map bounds: fall back to the take-off fix.
    const origin = inRange(home) ? home : (inRange(takeoff) ? takeoff : null);

    // An "H" pad marking where the aircraft took off. A symbol layer rather
    // than a DOM marker, so it can sit below the flight track and scale with the
    // zoom instead of staying a fixed size that dwarfs the path zoomed out.
    const takeoffPt = inRange(takeoff) ? takeoff : origin;
    if (takeoffPt) {
      if (!this.map.hasImage('takeoff-pad')) {
        this.map.addImage('takeoff-pad', padImage(), { pixelRatio: 2 });
      }
      this.map.addSource('takeoff', {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: { type: 'Point', coordinates: [takeoffPt.lon, takeoffPt.lat] },
        },
      });
      this.map.addLayer(
        {
          id: 'takeoff-pad',
          type: 'symbol',
          source: 'takeoff',
          layout: {
            'icon-image': 'takeoff-pad',
            'icon-size': ['interpolate', ['linear'], ['zoom'], 11, 0.3, 15, 0.6, 19, 1.0],
            'icon-allow-overlap': true,
            'icon-ignore-placement': true,
          },
        },
        'track'
      );
    }

    // The aircraft is a DOM marker carrying the same drone drawing as the
    // orientation panel, pinned to its position and rotated to its heading, so
    // the two read as the same aircraft.
    //
    // The trade-off, chosen deliberately: a marker projects a ground coordinate
    // and has no elevation, so on a pitched map it sits at its ground position
    // rather than floating at altitude. The shadow below still marks that
    // position, and the default view looks straight down, where ground and
    // altitude coincide. An extruded glyph placed it correctly in 3D but could
    // never look like the flat icon; this is the other side of that trade.
    //
    // rotationAlignment 'map' keeps the heading geographic: rotate the map and
    // the drone turns with it, so its nose always points where it was pointing.
    const droneEl = document.createElement('div');
    droneEl.className = 'map-drone';
    droneEl.style.width = '46px';
    droneEl.style.height = '46px';
    droneEl.style.pointerEvents = 'none';
    droneEl.innerHTML = droneSvg();

    // The SVG carries only a viewBox, so give it an explicit size here rather
    // than relying on the stylesheet reaching the marker.
    const droneSvgEl = droneEl.querySelector('svg');
    if (droneSvgEl) {
      droneSvgEl.style.display = 'block';
      droneSvgEl.style.width = '100%';
      droneSvgEl.style.height = '100%';
      droneSvgEl.style.overflow = 'visible';
    }

    this.aircraftMarker = new maplibregl.Marker({ element: droneEl, rotationAlignment: 'map' })
      .setLngLat(origin ? [origin.lon, origin.lat] : [0, 0])
      .addTo(this.map);

    // A ground dot underneath, so position stays readable when the aircraft
    // is high enough that the glyph is hard to place against the map.
    this.map.addSource('aircraft-shadow', {
      type: 'geojson',
      data: emptyCollection(),
      ...FINE_SOURCE,
    });

    this.map.addLayer({
      id: 'aircraft-shadow',
      type: 'circle',
      source: 'aircraft-shadow',
      paint: {
        // A shadow, not a marker. Soft edged, dark, and no outline: a hard
        // ring reads as a second object rather than as the absence of light.
        //
        // It also spreads and fades with height, the way a real one does,
        // which turns it into a depth cue. On a pitched map the aircraft and
        // its ground position separate visually, and the gap between them is
        // hard to read as altitude without this.
        'circle-radius': [
          'interpolate', ['linear'], ['get', 'height'],
          0, 3.5,
          120, 11,
        ],
        'circle-color': '#000000',
        'circle-opacity': [
          'interpolate', ['linear'], ['get', 'height'],
          0, 0.45,
          120, 0.14,
        ],
        'circle-blur': 0.7,
        'circle-pitch-alignment': 'map',
      },
    });
  }

  firstPosition() {
    const path = this.flight.path();
    return path.length > 0 ? [path[0][0], path[0][1]] : [0, 0];
  }

  /** Move the aircraft to a row index. Called on every scrub tick. */
  setIndex(index) {
    if (!this.ready || !this.flight || !this.aircraftMarker) return;

    const { latitude, longitude, heightM } = this.flight.cols;
    const lat = latitude[index];
    const lon = longitude[index];

    if (lat === null || lon === null) return;

    this._lastIndex = index;

    this._aircraftIndex = index;

    const height = heightM[index] === null ? 0 : Math.max(0, heightM[index]);

    // Pin the drone to its position and rotate it to its heading, then float it
    // up the screen by its altitude. A marker has no elevation of its own, so
    // this offset stands in for it: nothing when looking straight down, more as
    // the map pitches, recomputed on move below. The shadow stays on the ground.
    this.aircraftMarker
      .setLngLat([lon, lat])
      .setRotation(this.heading(index))
      .setOffset([0, -this.screenLift(lat, height)]);
    this.map.getSource('aircraft-shadow').setData({
      type: 'Feature',
      properties: { height },
      geometry: { type: 'Point', coordinates: [lon, lat] },
    });

    // Nudge the camera only when the aircraft leaves the viewport, rather than
    // centring on every tick. Centring fights the initial framing and makes
    // scrubbing feel like the map is dragging you around.
    if (this.follow) this.keepInView(lat, lon, height);
  }

  /**
   * Pan when the AIRCRAFT approaches the edge, not its shadow.
   *
   * getBounds().contains() tests a ground coordinate, which is where the
   * shadow sits. On a pitched map the aircraft is drawn at altitude and
   * therefore projects further up the screen, so it can be long gone off the
   * top while its ground position is still comfortably inside the bounds.
   * Zoomed in on a high flight the gap between the two is most of the screen.
   */
  keepInView(lat, lon, height) {
    const canvas = this.map.getCanvas();
    const width = canvas.clientWidth || canvas.width;
    const viewportHeight = canvas.clientHeight || canvas.height;

    if (!width || !viewportHeight) return;

    const ground = this.map.project([lon, lat]);
    const lift = this.screenLift(lat, height);
    const aircraftY = ground.y - lift;

    // A margin, so the pan starts before the aircraft is against the glass.
    // Waiting for it to actually leave means watching it disappear and come
    // back, which reads as a stutter rather than as tracking.
    const margin = Math.min(90, Math.min(width, viewportHeight) * 0.18);

    const outside = ground.x < margin
      || ground.x > width - margin
      || aircraftY < margin
      || aircraftY > viewportHeight - margin;

    if (!outside) return;

    // Offsetting the target puts the AIRCRAFT in the middle rather than its
    // ground position: without this a high flight recentres with the aircraft
    // still near the top edge and pans again almost immediately.
    this.map.easeTo({
      center: [lon, lat],
      offset: [0, Math.min(lift, viewportHeight * 0.3)],
      duration: 300,
    });
  }

  /**
   * How far up the screen an object at `height` metres appears, in pixels.
   *
   * A vertical line of length h projects to roughly h x sin(pitch) on screen:
   * nothing when looking straight down, most of its length near the horizon.
   * Ignores perspective foreshortening, which is a fraction of a pixel at the
   * altitudes and pitches a drone log produces.
   */
  screenLift(lat, height) {
    if (!height || height <= 0) return 0;

    const pitch = (this.map.getPitch() * Math.PI) / 180;
    const metresPerPixel = groundResolution(lat, this.map.getZoom());

    return (height * Math.sin(pitch)) / metresPerPixel;
  }

  /**
   * Recompute the marker's altitude offset for the current row, so it keeps
   * floating at its height as the view is pitched or zoomed, not only when the
   * row changes.
   */
  updateAircraftLift() {
    if (!this.aircraftMarker || !this.flight || this._aircraftIndex === undefined) return;

    const i = this._aircraftIndex;
    const lat = this.flight.cols.latitude[i];
    if (lat === null || lat === undefined) return;

    const raw = this.flight.cols.heightM[i];
    const height = raw === null || raw === undefined ? 0 : Math.max(0, raw);
    this.aircraftMarker.setOffset([0, -this.screenLift(lat, height)]);
  }

  /**
   * Which way the aircraft is pointing, in degrees clockwise from north.
   *
   * The nose direction (yaw), where the aircraft was pointing, rather than where
   * it was going. The track line already shows the direction of travel, so
   * course over ground would only restate the path; a multirotor can fly
   * sideways or backwards, and yaw is the part the path cannot show. Falls back
   * to course over ground only when the log has no yaw, so an attitude-less
   * flight still points somewhere sensible.
   */
  heading(index) {
    const { latitude, longitude, speedHorizontalMs, yawDeg } = this.flight.cols;

    if (yawDeg && yawDeg[index] !== null && yawDeg[index] !== undefined) {
      return yawDeg[index];
    }

    const speed = speedHorizontalMs ? speedHorizontalMs[index] : null;
    if (speed !== null && speed < 0.5) {
      return 0;
    }

    // Look a few rows back for a point far enough away to give a stable
    // bearing. Adjacent GPS fixes are noisy at low speed.
    for (let back = 1; back <= 12; back++) {
      const previous = index - back;
      if (previous < 0) break;

      const lat1 = latitude[previous];
      const lon1 = longitude[previous];
      const lat2 = latitude[index];
      const lon2 = longitude[index];

      if (lat1 === null || lon1 === null || lat2 === null || lon2 === null) continue;
      if (Math.abs(lat2 - lat1) < 1e-7 && Math.abs(lon2 - lon1) < 1e-7) continue;

      return bearing(lat1, lon1, lat2, lon2);
    }

    return 0;
  }

  setFollow(on) {
    this.follow = on;
  }

  /**
   * Frame the whole flight.
   *
   * fitBounds and pitch do not mix: asked to fit a box on a pitched camera,
   * MapLibre solves for the projected quadrilateral and the flight ends up
   * small and far away. So frame it flat, then pitch around the centre and
   * give back a little zoom to cover what the tilt pushes off screen.
   */
  fit(path, padding = 60) {
    const bounds = path.reduce(
      (b, p) => b.extend([p[0], p[1]]),
      new maplibregl.LngLatBounds([path[0][0], path[0][1]], [path[0][0], path[0][1]])
    );

    this.map.fitBounds(bounds, {
      padding,
      duration: 0,
      maxZoom: 19,
      pitch: 0,
      bearing: 0,
    });

    // Straight down by default.
    //
    // A pitched view spends vertical space on perspective, makes distances
    // hard to judge, and puts the curtain between the camera and everything
    // behind it. Tilting is available and is a deliberate act; the plan view
    // is what a flight path wants to be read as most of the time.
    this.map.easeTo({ pitch: 0, bearing: 0, duration: 0 });

    if (this.tiltControl) this.tiltControl.set(false);

    this._framedZoom = this.map.getZoom();
    this._bounds = bounds;
  }

  /** Re-frame the whole flight. Bound to a button and to the "f" key. */
  reframe() {
    if (this.flight) this.fit(this.flight.simplifiedPath(1.2));
  }

  /**
   * Toggle between the tilted view and straight down.
   *
   * MapLibre's own compass control only resets pitch to zero; it has no notion
   * of restoring it, so clicking it a second time does nothing. This keeps the
   * previous angle and puts it back.
   */
  toggleTilt() {
    if (!this.ready) return this.isTilted();

    const tilted = this.isTilted();

    if (tilted) {
      this._restorePitch = this.map.getPitch();
      this.map.easeTo({ pitch: 0, duration: 350 });
    } else {
      this.map.easeTo({ pitch: this._restorePitch || 55, duration: 350 });
    }

    if (this.tiltControl) this.tiltControl.set(!tilted);

    return !tilted;
  }

  isTilted() {
    return this.ready && this.map.getPitch() > 5;
  }

  /**
   * Whether the altitude curtain is drawn.
   *
   * Remembered, because it is a reading preference rather than a per-flight
   * choice: someone who finds the curtain cluttering should not have to say
   * so on every log they open.
   */
  /** Which basemap is showing. */
  basemap() {
    try {
      const stored = localStorage.getItem(BASEMAP_KEY);

      return stored && BASEMAPS[stored] ? stored : 'map';
    } catch {
      return 'map';
    }
  }

  /**
   * Swap the base map, keeping the flight on it.
   *
   * setStyle replaces every source and layer, so the flight has to be added
   * back afterwards. The camera survives, which is what matters: swapping the
   * backdrop should not move the view.
   */
  setBasemap(name) {
    if (!this.basemaps[name] || !this.ready) return this.basemap();

    try {
      localStorage.setItem(BASEMAP_KEY, name);
    } catch {
      /* a preference that cannot be saved is still worth applying */
    }

    const flight = this.flight;
    const camera = {
      center: this.map.getCenter(),
      zoom: this.map.getZoom(),
      pitch: this.map.getPitch(),
      bearing: this.map.getBearing(),
    };

    this.map.once('styledata', () => {
      if (!flight) return;

      this.flight = null;
      this.setFlight(flight);
      this.map.jumpTo(camera);

      if (this._aircraftIndex !== undefined) this.setIndex(this._aircraftIndex);
    });

    this.map.setStyle(this.basemaps[name].style);

    return name;
  }

  curtainVisible() {
    try {
      const stored = localStorage.getItem(CURTAIN_KEY);

      // Off unless asked for. The default view looks straight down, where a
      // curtain is a band of colour under the trace and adds nothing; it earns
      // its place once the camera is tilted, which is a deliberate act.
      return stored === '1';
    } catch {
      return false;
    }
  }

  toggleCurtain() {
    if (!this.ready) return false;

    const next = !this.curtainVisible();

    try {
      localStorage.setItem(CURTAIN_KEY, next ? '1' : '0');
    } catch {
      /* a preference that cannot be saved is still worth applying */
    }

    this.applyCurtain(next);

    if (this.curtainControl) this.curtainControl.set(next);

    return next;
  }

  /**
   * Show or hide the altitude rendering.
   *
   * The cap goes with the wall. A ribbon floating with nothing beneath it
   * reads as a fault rather than a choice, and turning the curtain off is a
   * request for a plan view, which the ground track already is.
   */
  applyCurtain(shown) {
    if (!this.ready) return;

    for (const id of ['curtain', 'track3d']) {
      if (this.map.getLayer(id)) {
        this.map.setLayoutProperty(id, 'visibility', shown ? 'visible' : 'none');
      }
    }

    // The ground track carries the path on its own once the ribbon is gone,
    // so it stops being a faint locator and becomes the trace.
    if (this.map.getLayer('track')) {
      this.map.setPaintProperty('track', 'line-width', shown ? 1 : 2);
      this.map.setPaintProperty('track', 'line-opacity', shown ? 0.35 : 0.95);
    }
  }

  clear() {
    if (!this.ready) return;

    for (const id of ['track', 'track3d', 'curtain', 'ribbon']) {
      if (this.map.getLayer(id)) this.map.removeLayer(id);
      if (this.map.getSource(id)) this.map.removeSource(id);
    }

    if (this.map.getLayer('takeoff-pad')) this.map.removeLayer('takeoff-pad');
    for (const id of ['aircraft-shadow', 'takeoff']) {
      if (this.map.getLayer(id)) this.map.removeLayer(id);
      if (this.map.getSource(id)) this.map.removeSource(id);
    }

    this._ribbonHeights = null;

    if (this.aircraftMarker) { this.aircraftMarker.remove(); this.aircraftMarker = null; }
  }

  resize() {
    if (this.ready) this.map.resize();
  }

  /**
   * A PNG of the current view, for the printed report.
   *
   * Captured inside a render callback rather than by setting
   * preserveDrawingBuffer at construction. That flag makes the capture work
   * from anywhere, but it also disables optimisations for the entire session,
   * and paying a frame-rate cost on every scrub for the sake of a button
   * pressed occasionally is a poor trade.
   *
   * Returns null rather than throwing. A report without a map is worth having;
   * a failed export because the canvas was tainted is not.
   */
  /**
   * A PNG of the current view.
   *
   * @param {object} options
   * @param {boolean} options.topDown flatten the camera before capturing
   *
   * A printed page has no camera to rotate, so a pitched view costs vertical
   * space to perspective and makes distances impossible to judge. Straight
   * down is the right projection for a document: it reads as a plan, which is
   * what a flight path on paper wants to be.
   */
  async snapshot(options = {}) {
    if (!this.ready || !this.flight) return null;

    const timeoutMs = options.timeoutMs || 8000;

    // The report always gets the same view: straight down, street map, no
    // curtain.
    //
    // Not because it matches the screen, but because it suits paper. A
    // pitched view spends vertical space on perspective and makes distances
    // unjudgeable. Satellite imagery prints dark and heavy and swallows the
    // trace. A curtain drawn from directly above is a band of colour with
    // nothing to say. Whatever the reader had on screen, the printed map
    // should be the legible one.
    const restore = options.topDown
      ? {
        pitch: this.map.getPitch(),
        bearing: this.map.getBearing(),
        // What is actually on screen, not the stored preference: the report must
        // put the map back exactly as the reader had it, and the two can differ.
        curtain: this.map.getLayer('curtain')
          ? this.map.getLayoutProperty('curtain', 'visibility') !== 'none'
          : this.curtainVisible(),
        basemap: this.basemap(),
      }
      : null;

    if (restore) {
      if (restore.basemap !== 'map') {
        await this.swapBasemapForSnapshot('map');
      }

      // After the swap: the basemap re-draw re-asserts the curtain from the
      // stored preference, so hiding it here (last) is what actually keeps it
      // out of the report image.
      this.applyCurtain(false);

      this.map.jumpTo({ pitch: 0, bearing: 0 });
      // A larger buffer than the interactive view: the report is a fixed frame
      // that cannot be panned, so the whole path has to sit comfortably inside
      // it rather than running to the edge.
      this.fit(this.flight.simplifiedPath(1.2), 130);
    }

    // Every camera move and style change is done now. Wait for the *final* view
    // to finish loading its tiles before grabbing the canvas. reframe() above
    // requested a fresh set for the flattened, refitted view, and capturing the
    // next frame regardless is what was putting half-drawn maps in the report.
    await this.whenTilesReady(timeoutMs);

    const image = await this.capture(timeoutMs);

    if (restore) {
      if (restore.basemap !== 'map') {
        await this.swapBasemapForSnapshot(restore.basemap);
      }

      // Force the curtain back to exactly its pre-report state, after the
      // basemap re-draw (which re-asserts it from the preference). Unconditional
      // and last, so a report can never leave the live curtain switched on — or
      // off — differently from how the reader had it.
      this.applyCurtain(restore.curtain);

      this.map.jumpTo({ pitch: restore.pitch, bearing: restore.bearing });
      this.reframe();
    }

    return image;
  }

  /**
   * Change basemap and resolve once the new style is installed.
   *
   * setStyle is asynchronous, and setBasemap re-adds the flight and restores the
   * camera on `styledata`. This waits for that to have happened so the caller
   * can safely move the camera on the rebuilt style. It deliberately does NOT
   * wait for tiles to draw: whenTilesReady does that once, after all the camera
   * moves, rather than after every intermediate step. Waiting for `idle` here
   * was the old approach and it could settle on a stale idle fired before the
   * reframe requested its tiles.
   */
  swapBasemapForSnapshot(name) {
    return new Promise((resolve) => {
      let settled = false;

      const done = () => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve();
      };

      // A tile server that never answers must not hang the export.
      const timer = setTimeout(done, 6000);

      // setBasemap registers its own styledata handler after this one, and uses
      // it to re-add the flight and restore the camera. Deferring to the next
      // task lets that handler run first, so the style is fully rebuilt by the
      // time we resolve.
      this.map.once('styledata', () => setTimeout(done, 0));

      this.setBasemap(name);
    });
  }

  /**
   * Resolve when the current view has finished loading its tiles.
   *
   * `idle` means no camera transition is running and nothing is still loading;
   * `areTilesLoaded()` confirms the viewport is actually covered. An idle that
   * fires before the reframed tiles were even requested fails the tile check, so
   * we keep listening and settle on the next one that passes both. The timeout
   * is the backstop for a tile server that never answers: a stalled provider
   * then slows the report rather than hanging it.
   */
  whenTilesReady(timeoutMs) {
    return new Promise((resolve) => {
      if (!this.ready) {
        resolve();
        return;
      }

      let settled = false;

      const finish = () => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        this.map.off('idle', check);
        resolve();
      };

      const check = () => {
        if (this.map.areTilesLoaded()) finish();
      };

      const timer = setTimeout(finish, timeoutMs);

      this.map.on('idle', check);

      // A camera move usually schedules the render that leads to idle, but force
      // one in case the view was already settled and nothing else would fire.
      this.map.triggerRepaint();
    });
  }

  capture(timeoutMs) {
    return new Promise((resolve) => {
      let settled = false;

      const finish = (value) => {
        if (settled) return;
        settled = true;
        resolve(value);
      };

      // The canvas is cleared after each frame, so toDataURL has to run inside
      // the render callback, before the browser reclaims the buffer.
      this.map.once('render', () => {
        try {
          finish(this.map.getCanvas().toDataURL('image/png'));
        } catch (error) {
          // Cross-origin tiles can taint the canvas. Nothing to be done about
          // it here, and it should not stop the report.
          console.warn('[logviewer] map capture failed:', error);
          finish(null);
        }
      });

      this.map.triggerRepaint();
      setTimeout(() => finish(null), timeoutMs);
    });
  }
}

function dot(colour, size) {
  const el = document.createElement('div');
  el.style.cssText = `width:${size}px;height:${size}px;border-radius:50%;background:${colour};
    border:1.5px solid #0a0d10;box-shadow:0 0 0 1px rgba(0,0,0,0.4)`;
  return el;
}

/**
 * The "H" take-off pad, rasterised to an image so the map can render it as a
 * symbol layer. A symbol can sit below the flight track (a DOM marker always
 * draws above the canvas) and scales with the zoom. Orange fill, black rim,
 * black H.
 */
function padImage() {
  const s = 48;
  const c = document.createElement('canvas');
  c.width = s;
  c.height = s;
  const ctx = c.getContext('2d');

  ctx.beginPath();
  ctx.arc(s / 2, s / 2, s / 2 - 4, 0, Math.PI * 2);
  ctx.fillStyle = '#ff8f00';
  ctx.fill();
  ctx.lineWidth = 4;
  ctx.strokeStyle = '#0a0d10';
  ctx.stroke();

  ctx.fillStyle = '#0a0d10';
  ctx.font = '700 26px Arial, Helvetica, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('H', s / 2, s / 2 + 1);

  return ctx.getImageData(0, 0, s, s);
}

/**
 * The tilt toggle, labelled with the view you are in.
 *
 * Lives with the zoom controls because that is where people look for camera
 * controls. It replaces MapLibre's compass, which only resets pitch and
 * bearing and gives no indication of what pressing it will do.
 */
class TiltControl {
  constructor(flightMap) {
    this.flightMap = flightMap;
  }

  onAdd() {
    this.container = document.createElement('div');
    this.container.className = 'maplibregl-ctrl maplibregl-ctrl-group';
    this.container.innerHTML =
      '<button type="button" class="tilt-toggle" title="Tilt the view (T)" '
      + 'aria-label="Tilt the view" aria-pressed="false">2D</button>';

    this.button = this.container.querySelector('button');
    this.button.addEventListener('click', () => this.set(this.flightMap.toggleTilt()));

    return this.container;
  }

  /** Called by the map so the label stays right however the tilt changed. */
  set(tilted) {
    if (!this.button) return;

    this.button.textContent = tilted ? '3D' : '2D';
    this.button.setAttribute('aria-pressed', String(tilted));
  }

  onRemove() {
    this.container.remove();
  }
}

/**
 * Base map switch.
 *
 * Two states rather than a menu: there are two, and a menu for two choices is
 * a click more than the job needs.
 */
class BasemapControl {
  constructor(flightMap) {
    this.flightMap = flightMap;
  }

  onAdd() {
    this.container = document.createElement('div');
    this.container.className = 'maplibregl-ctrl maplibregl-ctrl-group';
    this.container.innerHTML =
      '<button type="button" class="basemap-toggle" '
      + 'title="Switch between map and satellite (M)" '
      + 'aria-label="Switch between map and satellite">'
      + '<i class="fa-solid fa-earth-europe" aria-hidden="true"></i></button>';

    this.button = this.container.querySelector('button');
    this.button.addEventListener('click', () => {
      const next = this.flightMap.basemap() === 'map' ? 'satellite' : 'map';
      this.set(this.flightMap.setBasemap(next));
    });

    this.set(this.flightMap.basemap());

    return this.container;
  }

  set(name) {
    if (!this.button) return;

    const satellite = name === 'satellite';

    // The icon names what you are looking at, not what you would switch to.
    this.button.innerHTML = satellite
      ? '<i class="fa-solid fa-satellite" aria-hidden="true"></i>'
      : '<i class="fa-solid fa-earth-europe" aria-hidden="true"></i>';

    this.button.setAttribute('aria-pressed', String(satellite));
  }

  onRemove() {
    this.container.remove();
  }
}

/**
 * The altitude curtain toggle.
 *
 * Sits with the camera controls because that is what it is: a choice about
 * how much of the third dimension to draw, not a property of the flight.
 *
 * The icon is the curtain itself, a wall under a path, so it reads without a
 * label at the size a map control allows.
 */
class CurtainControl {
  constructor(flightMap) {
    this.flightMap = flightMap;
  }

  onAdd() {
    this.container = document.createElement('div');
    this.container.className = 'maplibregl-ctrl maplibregl-ctrl-group';
    this.container.innerHTML = `
      <button type="button" class="curtain-toggle"
              title="Show or hide the altitude curtain (C)"
              aria-label="Show or hide the altitude curtain" aria-pressed="true">
        <i class="fa-solid fa-chart-area" aria-hidden="true"></i>
      </button>`;

    this.button = this.container.querySelector('button');
    this.button.addEventListener('click', () => this.set(this.flightMap.toggleCurtain()));
    this.set(this.flightMap.curtainVisible());

    return this.container;
  }

  set(shown) {
    if (!this.button) return;

    this.button.setAttribute('aria-pressed', String(shown));
    this.button.classList.toggle('off', !shown);
  }

  onRemove() {
    this.container.remove();
  }
}

/**
 * One extruded quad per path segment, offset perpendicular to its own
 * direction of travel.
 *
 * The offset has to be perpendicular and measured in metres. Offsetting by a
 * fixed number of degrees on both axes, as this did before, produced a ribbon
 * whose width depended on which way the aircraft was flying: near zero on a
 * north-east heading, where the offset ran parallel to the segment, and widest
 * on a north-west one. It was latitude-distorted too, since a degree of
 * longitude is shorter than a degree of latitude everywhere but the equator.
 */
export function curtainGeometry(path, widthMetres = PATH_WIDTH_M, options = {}) {
  const features = [];
  const half = widthMetres / 2;

  // How much altitude one step is allowed to cover.
  //
  // A step is invisible once it is smaller than a screen pixel. A whole flight
  // frames at zoom 16 to 18, which is 1.5 down to 0.37 metres per pixel, so a
  // quarter of a metre is already below the threshold at the tightest of those
  // and well below it at the others. Smaller buys nothing anyone can see.
  const target = options.stepMetres || STEP_METRES;

  // Total climb and descent, used to keep the geometry bounded. Per-segment
  // caps do not: a flight with two hundred climbing segments passes every one
  // of them and still produces tens of thousands of quads.
  let totalRise = 0;

  for (let i = 1; i < path.length; i++) {
    totalRise += Math.abs((path[i][2] || 0) - (path[i - 1][2] || 0));
  }

  // Coarsen only if the budget would be exceeded, so ordinary flights get the
  // fine step and a pathological one degrades rather than stalling the map.
  const budget = options.maxQuads || MAX_CURTAIN_QUADS;
  const stepMetres = Math.max(target, totalRise / Math.max(1, budget - path.length));

  for (let i = 1; i < path.length; i++) {
    const [lon1, lat1, h1] = path[i - 1];
    const [lon2, lat2, h2] = path[i];

    const along = bearing(lat1, lon1, lat2, lon2);
    const left = along - 90;
    const right = along + 90;

    // A fill-extrusion is flat topped: one height for the whole polygon, and
    // no way to slope it. So a segment that climbs would be drawn as a flat
    // slab at the average of its ends, metres from the truth at both. On a
    // takeoff simplified into one long segment, that puts the path halfway up
    // the climb while the aircraft is still on the ground.
    //
    // Cutting the segment into steps is the only way to approximate a ramp
    // with this layer type. It costs geometry only where the flight actually
    // changes altitude, so level flight stays at one quad per segment.
    const rise = Math.abs(h2 - h1);
    const steps = Math.max(1, Math.ceil(rise / stepMetres));

    for (let step = 0; step < steps; step++) {
      const t0 = step / steps;
      const t1 = (step + 1) / steps;

      const a = [lon1 + (lon2 - lon1) * t0, lat1 + (lat2 - lat1) * t0];
      const b = [lon1 + (lon2 - lon1) * t1, lat1 + (lat2 - lat1) * t1];

      const height = Math.max(0.5, h1 + (h2 - h1) * ((t0 + t1) / 2));

      features.push({
        type: 'Feature',
        properties: { height },
        geometry: {
          type: 'Polygon',
          coordinates: [[
            destination(a[1], a[0], left, half),
            destination(b[1], b[0], left, half),
            destination(b[1], b[0], right, half),
            destination(a[1], a[0], right, half),
            destination(a[1], a[0], left, half),
          ]],
        },
      });
    }
  }

  return { type: 'FeatureCollection', features };
}

/**
 * The height at which the ribbon is actually drawn, for every original row.
 *
 * The ribbon is built from a simplified path, so its height at a given moment
 * is not the aircraft's recorded height at that moment. This maps back from
 * full-resolution row index to the quad covering it, which is what lets the
 * aircraft be placed reliably on top of its own path.
 *
 * Overlapping segments take the highest, so a crossing point never buries it.
 */
export function ribbonHeightByRow(simplifiedPath, rowCount) {
  const heights = new Float32Array(rowCount);

  for (let i = 1; i < simplifiedPath.length; i++) {
    const a = simplifiedPath[i - 1];
    const b = simplifiedPath[i];

    const from = Math.max(0, Math.min(rowCount - 1, a[3]));
    const to = Math.max(0, Math.min(rowCount - 1, b[3]));
    const span = Math.max(1, to - from);

    // Interpolated along the segment, matching the stepped ribbon. Taking the
    // average of the endpoints instead reported one height for the whole
    // segment, so an aircraft cleared against it during a climb was lifted to
    // the middle of that climb and floated above the ground at take-off.
    for (let row = from; row <= to; row++) {
      const height = Math.max(0.5, a[2] + (b[2] - a[2]) * ((row - from) / span));

      if (height > heights[row]) heights[row] = height;
    }
  }

  return heights;
}

/** Exposed for tests and for anything that needs to reason about the path. */
export const pathWidthMetres = PATH_WIDTH_M;
export const pathThicknessMetres = PATH_THICKNESS_M;
export const aircraftSizeMetres = AIRCRAFT_SIZE_M;

function emptyCollection() {
  return { type: 'FeatureCollection', features: [] };
}

/**
 * A quadcopter at flight altitude, oriented along `headingDeg`.
 *
 * Body, four arms and four rotor discs. Front pair green, rear pair red,
 * following the LED convention on the aircraft itself, so orientation reads
 * at a glance from any camera angle without needing to work out which end is
 * which.
 *
 * Returns a FeatureCollection because each part carries its own colour and
 * extrusion height, and a fill-extrusion layer reads those per feature.
 */
export function aircraftGeometry(lat, lon, headingDeg, height, sizeMetres = AIRCRAFT_SIZE_M) {
  // sizeMetres is the overall tip-to-tip span, so the caller's number is the
  // number you could measure on screen. Deriving it from the arm length
  // instead left the real extent 23% larger than asked for, which quietly
  // broke the ratio this whole model is sized by.
  //
  // Rotors sit on arms at 45 degrees, so each contributes cos(45) of the arm
  // length plus its own radius to the half-span. With the rotor radius set to
  // half the arm length, half-span = arm * (cos45 + 0.5), which inverts to the
  // factors below.
  const arm = sizeMetres * 0.4142;
  const rotor = arm * 0.5;
  const features = [];

  // Every thickness is a proportion of the span, so the model keeps its
  // proportions at any size.
  const armThickness = sizeMetres * 0.06;
  const rotorThickness = sizeMetres * 0.05;
  const bodyThickness = sizeMetres * 0.24;

  const part = (coordinates, colour, thickness, lift = 0) => {
    features.push({
      type: 'Feature',
      properties: { base: height + lift, top: height + lift + thickness, colour },
      geometry: { type: 'Polygon', coordinates: [coordinates] },
    });
  };

  // A ring: an outer circle with an inner circle punched out as a hole, so a
  // rotor reads as a thin outline rather than a solid blob. If a renderer
  // ignores the hole it degrades to a plain disc, which the coloured hub then
  // still sits on top of, so the worst case is a grey disc with a hub, not a
  // colour blob.
  const annulus = (cLat, cLon, outerR, innerR, colour, thickness, lift = 0) => {
    features.push({
      type: 'Feature',
      properties: { base: height + lift, top: height + lift + thickness, colour },
      geometry: {
        type: 'Polygon',
        coordinates: [disc(cLat, cLon, outerR, 24), disc(cLat, cLon, innerR, 24).slice().reverse()],
      },
    });
  };

  // Arms first so the body and rotors draw over them
  const arms = [45, 135, 225, 315];

  for (const offset of arms) {
    const angle = headingDeg + offset;
    const tip = destination(lat, lon, angle, arm);
    const width = sizeMetres * 0.038;

    part(
      [
        destination(lat, lon, angle + 90, width),
        destination(tip[1], tip[0], angle + 90, width),
        destination(tip[1], tip[0], angle - 90, width),
        destination(lat, lon, angle - 90, width),
        destination(lat, lon, angle + 90, width),
      ],
      ARM_GREY,
      armThickness
    );
  }

  // Rotors as thin grey rings with a small coloured hub at the centre, to match
  // the orientation model rather than the solid coloured discs that read as
  // blobs. Front hubs green, rear red, following the LED convention.
  const hole = rotor * 0.58;
  const hub = hole * 0.78;
  for (const offset of arms) {
    const angle = headingDeg + offset;
    const centre = destination(lat, lon, angle, arm);
    const isFront = offset === 45 || offset === 315;

    annulus(centre[1], centre[0], rotor, hole, ARM_GREY, rotorThickness, armThickness);
    part(
      disc(centre[1], centre[0], hub, 16),
      isFront ? ROTOR_FRONT : ROTOR_REAR,
      rotorThickness,
      armThickness
    );
  }

  // Body last and tallest. A rectangle with its short edges fore and aft, so
  // the airframe itself indicates which way it is facing even before you read
  // the rotor colours.
  part(
    rectangle(lat, lon, headingDeg, arm * 1.15, arm * 0.52),
    BODY_GREY,
    bodyThickness
  );

  // Nose marker: a slim accent triangle ahead of the body, so the front reads
  // as a point the way the orientation model does. Base just inside the body's
  // fore edge, apex extending past it, at body height so it caps the airframe.
  const noseBase = destination(lat, lon, headingDeg, arm * 0.5);
  const noseTip = destination(noseBase[1], noseBase[0], headingDeg, arm * 0.4);
  const noseHalf = arm * 0.17;
  const noseLeft = destination(noseBase[1], noseBase[0], headingDeg - 90, noseHalf);
  const noseRight = destination(noseBase[1], noseBase[0], headingDeg + 90, noseHalf);
  part([noseLeft, noseTip, noseRight, noseLeft], NOSE_ACCENT, bodyThickness);

  return { type: 'FeatureCollection', features };
}

/**
 * A rectangle centred on a point, aligned to a heading.
 *
 * `lengthMetres` runs fore and aft, `widthMetres` across, so the short edges
 * face front and back when length exceeds width.
 */
export function rectangle(lat, lon, headingDeg, lengthMetres, widthMetres) {
  const halfLength = lengthMetres / 2;
  const halfWidth = widthMetres / 2;

  const fore = destination(lat, lon, headingDeg, halfLength);
  const aft = destination(lat, lon, headingDeg + 180, halfLength);

  const foreLeft = destination(fore[1], fore[0], headingDeg - 90, halfWidth);
  const foreRight = destination(fore[1], fore[0], headingDeg + 90, halfWidth);
  const aftRight = destination(aft[1], aft[0], headingDeg + 90, halfWidth);
  const aftLeft = destination(aft[1], aft[0], headingDeg - 90, halfWidth);

  return [foreLeft, foreRight, aftRight, aftLeft, foreLeft];
}

/** A regular polygon approximating a circle, as a closed ring. */
function disc(lat, lon, radiusMetres, segments = 10) {
  const ring = [];

  for (let i = 0; i < segments; i++) {
    ring.push(destination(lat, lon, (360 / segments) * i, radiusMetres));
  }

  ring.push(ring[0]);
  return ring;
}

/** Point `metres` away from lat/lon along `bearingDeg`. Returns [lon, lat]. */
export function destination(lat, lon, bearingDeg, metres) {
  const rad = (bearingDeg * Math.PI) / 180;
  const dLat = (metres * Math.cos(rad)) / 111320;
  const dLon = (metres * Math.sin(rad)) / (111320 * Math.cos((lat * Math.PI) / 180));

  return [lon + dLon, lat + dLat];
}

/**
 * Metres per screen pixel at a latitude and zoom.
 *
 * The constant is the earth's circumference divided by the tile size, and the
 * tile size is the part that is easy to get wrong: MapLibre GL JS uses 512
 * pixel tiles, not the 256 of the older slippy-map convention. The widely
 * quoted 156543.03392 is the 256 figure, and using it here gives twice the
 * true resolution.
 *
 * Only an approximation of on-screen scale, and only near the centre of the
 * view: under pitch the scale varies across the viewport, so this is fine for
 * deciding when to pan and quite wrong for positioning anything.
 */
export function groundResolution(latitude, zoom) {
  const metresPerTile = 40075016.686 / 512;

  return (metresPerTile * Math.cos((latitude * Math.PI) / 180)) / Math.pow(2, zoom);
}

/** Initial bearing from one point to another, degrees clockwise from north. */
export function bearing(lat1, lon1, lat2, lon2) {
  const toRad = (d) => (d * Math.PI) / 180;
  const dLon = toRad(lon2 - lon1);
  const y = Math.sin(dLon) * Math.cos(toRad(lat2));
  const x =
    Math.cos(toRad(lat1)) * Math.sin(toRad(lat2)) -
    Math.sin(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.cos(dLon);

  return (((Math.atan2(y, x) * 180) / Math.PI) + 360) % 360;
}
